import React, { useEffect, useRef, useState } from 'react';
import { Menu, X, TreePine, ChevronDown, Phone } from 'lucide-react';
import { Button } from './ui/button';
import { companyInfo } from '../data/mock';
import { useLanguage } from '../context/LanguageContext';

const Header = () => {
  const { t, language, changeLanguage, languageOptions } = useLanguage();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isLanguageOpen, setIsLanguageOpen] = useState(false);
  const languageRef = useRef(null);

  const companySlogan = companyInfo.slogan;

  useEffect(() => {
    const handleOutside = (event) => {
      if (languageRef.current && !languageRef.current.contains(event.target)) {
        setIsLanguageOpen(false);
      }
    };

    document.addEventListener('mousedown', handleOutside);
    return () => document.removeEventListener('mousedown', handleOutside);
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { key: 'home', href: '#sakums', label: t('nav.home', 'Sākums') },
    { key: 'services', href: '#pakalpojumi', label: t('nav.services', 'Pakalpojumi') },
    { key: 'works', href: '#darbi', label: t('nav.works', 'Darbi') },
    { key: 'process', href: '#process', label: t('nav.process', 'Sadarbība') },
    { key: 'why', href: '#kapec', label: t('nav.why', 'Kāpēc mēs') },
    { key: 'about', href: '#par-mums', label: t('nav.about', 'Par mums') },
    { key: 'contact', href: '#kontakti', label: t('nav.contact', 'Kontakti') },
  ];

  const companyName = companyInfo.name;

  return (
    <header
      className={`fixed top-0 left-0 right-0 transition-all duration-500 ${isMenuOpen ? 'z-[70]' : 'z-50'} ${
        isScrolled
          ? 'bg-white/95 backdrop-blur-md shadow-lg shadow-[#1F3D2B]/5'
          : 'bg-transparent'
      }`}
      data-testid="main-header"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-24">
          {/* Logo */}
          <a href="#sakums" className="flex items-center gap-4 group" data-testid="header-logo-link">
            <div className="relative">
              <img
                src={isScrolled ? companyInfo.logo.dark : companyInfo.logo.transparent}
                alt={companyInfo.name}
                className="h-16 w-auto transition-transform duration-300 group-hover:scale-105"
              />
              {/* Glow effect on hover */}
              <div className="absolute inset-0 bg-[#1F3D2B]/20 rounded-full blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
            </div>
            <div className="flex flex-col">
              <span className={`text-2xl font-bold tracking-tight transition-colors duration-300 ${
                isScrolled ? 'text-[#1F3D2B]' : 'text-white'
              }`}>
                {companyName}
              </span>
              <span className={`text-sm font-medium transition-colors duration-300 ${
                isScrolled ? 'text-[#1F3D2B]/70' : 'text-white/80'
              }`}>
                {companySlogan}
              </span>
            </div>
          </a>

          {/* Desktop Navigation */}
          <nav className="hidden xl:flex items-center gap-1">
            {navLinks.map((link) => {
              const testId = `header-nav-link-${link.key}`;
              return (
                <a
                  key={link.href}
                  href={link.href}
                  className={`px-3 py-2 text-sm font-medium rounded-md transition-all duration-300 animated-underline ${
                    isScrolled
                      ? 'text-[#2E2E2E] hover:text-[#1F3D2B]'
                      : 'text-white/90 hover:text-white'
                  }`}
                  data-testid={testId}
                >
                  {link.label}
                </a>
              );
            })}
          </nav>

          {/* Desktop CTA */}
          <div className="hidden xl:flex items-center gap-4">
            <Button
              className={`transition-all duration-300 ${
                isScrolled
                  ? 'bg-white hover:bg-gray-50 text-[#1F3D2B] border border-[#1F3D2B]/20 hover:border-[#1F3D2B]/40'
                  : 'bg-white/10 backdrop-blur-sm hover:bg-white/20 text-white border border-white/30 hover:border-white/50'
              }`}
              asChild
            >
              <a href="#kontakti" data-testid="header-cta-consultation">{t('nav.consult', 'Pieteikties')}</a>
            </Button>
            <Button
              className="bg-gradient-to-r from-[#1F3D2B] to-[#2a5039] hover:from-[#2a5039] hover:to-[#1F3D2B] text-white shadow-lg shadow-[#1F3D2B]/30 hover:shadow-[#1F3D2B]/50 hover:scale-105 transition-all duration-300 font-semibold"
              asChild
            >
              <a href="/portal/login" data-testid="header-portal-login">🔐 {t('nav.portal', 'Klientu portāls')}</a>
            </Button>
            <div
              className="relative ml-2"
              data-testid="language-switcher"
              ref={languageRef}
            >
              <button
                type="button"
                onClick={() => setIsLanguageOpen((prev) => !prev)}
                className={`flex items-center gap-2 px-3 py-2 rounded-full text-xs font-semibold transition-all border ${
                  isScrolled
                    ? 'bg-white text-[#1F3D2B] border-[#1F3D2B]/20 hover:border-[#1F3D2B]/40'
                    : 'bg-white/10 text-white border-white/30 hover:border-white/60'
                }`}
                data-testid="language-switcher-toggle"
              >
                <img
                  src={languageOptions.find((option) => option.code === language)?.flagUrl}
                  alt={`${language.toUpperCase()} flag`}
                  className="w-4 h-4"
                  data-testid="language-current-flag"
                />
                <span data-testid="language-current-label">{language.toUpperCase()}</span>
                <ChevronDown className={`w-4 h-4 transition-transform ${isLanguageOpen ? 'rotate-180' : ''}`} />
              </button>
              {isLanguageOpen && (
                <div
                  className="absolute right-0 mt-2 w-40 bg-white/95 backdrop-blur-md border border-[#1F3D2B]/10 rounded-xl shadow-xl p-2 space-y-1 animate-fade-in"
                  data-testid="language-options-dropdown"
                >
                  {languageOptions.map((option) => (
                    <button
                      key={option.code}
                      onClick={() => {
                        changeLanguage(option.code);
                        setIsLanguageOpen(false);
                      }}
                      className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-semibold transition-all ${
                        language === option.code
                          ? 'bg-[#1F3D2B]/10 text-[#1F3D2B]'
                          : 'text-[#2E2E2E] hover:bg-[#1F3D2B]/5'
                      }`}
                      data-testid={`language-option-${option.code}`}
                    >
                      <img src={option.flagUrl} alt={`${option.label} flag`} className="w-4 h-4" />
                      <span>{option.label}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            className={`xl:hidden p-2 rounded-md transition-colors duration-300 ${
              isScrolled ? 'text-[#2E2E2E]' : 'text-white'
            }`}
            aria-label="Toggle menu" data-testid="header-mobile-menu-toggle"
          >
            {isMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Menu */}
        {isMenuOpen && (
          <div className="xl:hidden bg-white border-t border-gray-100 shadow-lg rounded-b-2xl animate-fade-in max-h-[calc(100dvh-6rem)] overflow-y-auto overscroll-contain" data-testid="header-mobile-menu">
            <nav className="py-4 px-2">
              {navLinks.map((link, index) => {
                const testId = `header-mobile-nav-link-${link.key}`;
                return (
                  <a
                    key={link.href}
                    href={link.href}
                    onClick={() => setIsMenuOpen(false)}
                    className="block px-4 py-3 text-[#2E2E2E] hover:bg-[#1F3D2B]/5 hover:text-[#1F3D2B] rounded-md transition-all duration-300"
                    style={{ animationDelay: `${index * 0.05}s` }}
                    data-testid={testId}
                  >
                    {link.label}
                  </a>
                );
              })}
              <div className="mt-4 px-4 pt-4 border-t border-gray-100 space-y-3">
                <a
                  href={`tel:${companyInfo.phone}`}
                  className="flex items-center gap-2 text-[#1F3D2B] font-medium mb-4"
                  data-testid="header-mobile-phone-link"
                >
                  <Phone className="w-4 h-4" />
                  {companyInfo.phone}
                </a>
                <div className="grid grid-cols-3 gap-2" data-testid="language-switcher-mobile">
                  {languageOptions.map((option) => (
                    <button
                      key={option.code}
                      onClick={() => changeLanguage(option.code)}
                      className={`px-2 py-2 rounded-xl text-xs font-semibold transition-all flex items-center justify-center gap-1.5 ${
                        language === option.code
                          ? 'bg-[#1F3D2B] text-white'
                          : 'bg-[#1F3D2B]/10 text-[#1F3D2B]'
                      }`}
                      data-testid={`language-option-mobile-${option.code}`}
                    >
                      <img src={option.flagUrl} alt={`${option.label} flag`} className="w-4 h-4" />
                      <span>{option.label}</span>
                    </button>
                  ))}
                </div>
                <Button className="w-full bg-gradient-to-r from-[#1F3D2B] to-[#2a5039] hover:from-[#2a5039] hover:to-[#1F3D2B] text-white font-semibold shadow-lg" asChild>
                  <a href="/portal/login" data-testid="header-mobile-portal-login">🔐 {t('nav.portal', 'Klientu portāls')}</a>
                </Button>
                <Button className="w-full bg-white hover:bg-gray-50 text-[#1F3D2B] border border-[#1F3D2B]/20" asChild>
                  <a href="#kontakti" data-testid="header-mobile-consultation">{t('nav.consultMobile', 'Pieteikties konsultācijai')}</a>
                </Button>
              </div>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
